import { useMemo } from "react";
import { Link } from "react-router-dom";
import { FaHeart, FaRegHeart } from "react-icons/fa";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { getProductMaxCartQuantity } from "../../context/CartContext";
import {
  getStorefrontPrices,
  productHasVariantOptions,
  getTotalVariantStock,
  productDetailPathSegment,
} from "../../utils/productVariants";
import { useFeatureFlags } from "../../context/FeatureFlagsContext";

function hashToUnitFloat(seed) {
  const str = String(seed ?? "");
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 4294967296;
}

/** Desktop: fixed action column. Mobile: full-width row under details. */
const PRODUCT_CARD_ACTION_COL = "w-full shrink-0 sm:w-[172px]";
const PRODUCT_CARD_CTA_ROW =
  "flex w-full min-h-[44px] items-center justify-center gap-1.5 rounded-full px-5 py-2.5 text-xs font-bold transition active:scale-[0.98] sm:min-h-[46px] sm:gap-2 sm:px-6 sm:py-3 sm:text-sm";

const PRODUCT_CARD_STEPPER_ROW =
  "flex w-full min-h-[44px] items-center justify-between gap-1 rounded-full bg-stone-100 px-1.5 py-1 sm:min-h-[46px] sm:px-2 sm:py-1.5";

const ProductCard = ({
  product,
  isWishlisted,
  onToggleWishlist,
  onAddToCart,
  cartItem,
  onIncrement,
  onDecrement,
  /** When product has variant options, cart may have multiple lines — total units for badge */
  variantCartQuantitySum = 0,
}) => {
  const { wishlistEnabled } = useFeatureFlags();
  const badge = product.isPriceCrash
    ? "Price crash"
    : product.isTopRated
      ? "Top rated"
      : "";

  const pathSegment = productDetailPathSegment(product);

  const hasVariants = productHasVariantOptions(product);
  const { salePrice, listPrice: listPriceForDiscount, hasDiscount } =
    getStorefrontPrices(product);

  const maxCartQty = hasVariants
    ? getTotalVariantStock(product)
    : getProductMaxCartQuantity(product);
  const hasStockCap = Number.isFinite(maxCartQty);
  const outOfStock = hasStockCap && maxCartQty <= 0;
  const atCartMax =
    !hasVariants &&
    Boolean(cartItem) &&
    hasStockCap &&
    cartItem.quantity >= maxCartQty;

  const fallback = useMemo(() => {
    const seed = product?._id ?? product?.id ?? product?.slug ?? product?.name;
    const u1 = hashToUnitFloat(seed);
    const u2 = hashToUnitFloat(`${seed}-reviews`);
    const rawRating = 3.8 + u1 * (4.5 - 3.8);
    const rating = Math.round(rawRating * 10) / 10;
    const reviews = 5 + Math.floor(u2 * 16);
    return { rating, reviews };
  }, [product?._id, product?.id, product?.slug, product?.name]);

  const shippingFee = Number(product.shippingFee ?? 100);
  const shippingLabel =
    shippingFee === 0 ? "Free delivery" : `Delivery ₹${shippingFee}`;

  const wishlistButton = (className) => (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggleWishlist(product._id);
      }}
      className={className}
      aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
    >
      {isWishlisted ? (
        <FaHeart className="text-lg text-rose-500 sm:text-xl" />
      ) : (
        <FaRegHeart className="text-lg text-stone-500 sm:text-xl" />
      )}
    </button>
  );

  return (
    <article className="group relative flex w-full flex-col gap-3 overflow-hidden rounded-2xl border border-stone-100 bg-white p-3 shadow-md transition-all duration-300 hover:border-rose-100 hover:shadow-lg sm:flex-row sm:gap-5 sm:p-4">
      {/* Image + details — mobile: stack (text full width, then image); sm+: image | text row */}
      <Link
        to={{ pathname: `/products/${pathSegment}` }}
        state={{ product }}
        className="relative flex min-w-0 w-full flex-1 flex-col gap-3 outline-none ring-offset-2 focus-visible:ring-2 focus-visible:ring-rose-400 sm:flex-row sm:gap-5"
        tabIndex={0}
        aria-label={`${product.name}, details`}
        style={{ textDecoration: "none", color: "inherit" }}
      >
        <div className="relative order-2 h-64 w-full shrink-0 overflow-hidden rounded-xl bg-gradient-to-br from-stone-100 to-orange-50 sm:order-none sm:h-[132px] sm:w-[132px]">
          <img
            src={product.images?.[0]}
            alt={product.name}
            className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
          />
          {badge && (
            <span className="pointer-events-none absolute left-2 top-2 z-10 rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white shadow sm:left-2.5 sm:top-2.5 sm:px-2.5 sm:text-[11px]">
              {badge}
            </span>
          )}
          {/* Mobile: wishlist on image so the action column can stay full-width CTA only */}
          {wishlistEnabled
            ? wishlistButton(
                "absolute right-2 top-2 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-white/95 shadow-md ring-1 ring-stone-200/80 backdrop-blur-sm transition hover:bg-white sm:hidden",
              )
            : null}
        </div>

        <div className="order-1 flex w-full min-w-0 flex-1 flex-col justify-center gap-1.5 py-0 sm:order-none sm:gap-2 sm:py-0.5 sm:pl-0.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-900 ring-1 ring-amber-100 sm:text-xs">
              {typeof product.averageRating === "number" &&
              product.averageRating > 0
                ? product.averageRating.toFixed(1)
                : fallback.rating.toFixed(1)}
              <span className="text-amber-500">★</span>
              <span className="font-normal text-amber-800/80">
                (
                {typeof product.numberOfReviews === "number" &&
                product.numberOfReviews > 0
                  ? product.numberOfReviews
                  : fallback.reviews}
                )
              </span>
            </span>
            <span
              className={`inline-flex max-w-full items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 sm:text-[11px] ${
                shippingFee === 0
                  ? "bg-emerald-50 text-emerald-800 ring-emerald-100"
                  : "bg-stone-50 text-stone-600 ring-stone-100"
              }`}
            >
              {shippingLabel}
            </span>
          </div>

          {product.brand && (
            <p className="text-[10px] font-bold uppercase tracking-wider text-stone-400 sm:text-[11px]">
              {product.brand}
            </p>
          )}

          <h3 className="line-clamp-2 text-[15px] font-semibold leading-snug text-stone-900 sm:text-base">
            {product.name}
          </h3>

          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="text-lg font-bold text-stone-900 sm:text-xl">
              {hasVariants ? (
                <span className="text-sm font-semibold text-stone-500">
                  From{" "}
                </span>
              ) : null}
              ₹{salePrice}
            </span>
            {hasDiscount && (
              <>
                <span className="text-sm text-stone-400 line-through">
                  ₹{listPriceForDiscount}
                </span>
                <span className="rounded-md bg-emerald-50 px-1.5 py-0.5 text-xs font-bold text-emerald-700">
                  {Math.round(
                    ((listPriceForDiscount - salePrice) / listPriceForDiscount) *
                      100,
                  )}
                  % off
                </span>
              </>
            )}
          </div>

          {product.couponPrice && (
            <p className="text-xs font-medium text-violet-700">
              With coupon: ₹{product.couponPrice}
            </p>
          )}
        </div>
      </Link>

      {/* Desktop: wishlist + cart column. Mobile: full-width CTA row under details. */}
      <div
        className={`flex flex-col items-stretch justify-between gap-2 border-t border-stone-100 pt-3 sm:border-t-0 sm:border-l sm:border-stone-100 sm:pl-4 sm:pt-0 ${PRODUCT_CARD_ACTION_COL}`}
      >
        {wishlistEnabled
          ? wishlistButton(
              "hidden h-9 w-9 shrink-0 items-center justify-center self-end rounded-full bg-stone-50 ring-1 ring-stone-200/80 transition hover:bg-white hover:shadow-md sm:flex sm:h-10 sm:w-10",
            )
          : null}

        <div className="min-w-0 w-full sm:mt-auto">
          {hasVariants ? (
            <div className="flex w-full flex-col gap-1.5 sm:gap-2">
              <Link
                to={{ pathname: `/products/${pathSegment}` }}
                state={{ product }}
                onClick={(e) => e.stopPropagation()}
                className={`${PRODUCT_CARD_CTA_ROW} border-2 border-rose-200 bg-white text-rose-600 shadow-md hover:bg-rose-50 hover:shadow-lg`}
                aria-label="Choose product option"
              >
                <ShoppingCartIcon className="shrink-0" style={{ fontSize: 18 }} />
                <span className="whitespace-nowrap sm:hidden">Options</span>
                <span className="hidden whitespace-nowrap sm:inline">
                  Choose option
                </span>
              </Link>
              {variantCartQuantitySum > 0 ? (
                <p className="text-center text-[10px] font-semibold leading-snug text-stone-500 sm:text-[11px]">
                  <span className="sm:hidden">
                    {variantCartQuantitySum} in bag ·{" "}
                    <Link
                      to="/checkout"
                      className="text-rose-600 underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Cart
                    </Link>
                  </span>
                  <span className="hidden sm:inline">
                    {variantCartQuantitySum} in bag —{" "}
                    <Link
                      to="/checkout"
                      className="text-rose-600 underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      checkout
                    </Link>
                  </span>
                </p>
              ) : null}
            </div>
          ) : cartItem ? (
            <div className={PRODUCT_CARD_STEPPER_ROW}>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDecrement();
                }}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-base font-bold text-stone-700 shadow-sm transition active:scale-95"
                aria-label="Decrease quantity"
              >
                −
              </button>
              <span className="min-w-[1.25rem] flex-1 text-center text-sm font-bold text-stone-900">
                {cartItem.quantity}
              </span>
              <button
                type="button"
                disabled={atCartMax}
                title={atCartMax ? `Max ${maxCartQty} in stock` : "Add one more"}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onIncrement();
                }}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-base font-bold text-stone-700 shadow-sm transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Increase quantity"
              >
                +
              </button>
            </div>
          ) : (
            <button
              type="button"
              disabled={outOfStock}
              title={outOfStock ? "Out of stock" : "Add to cart"}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onAddToCart();
              }}
              className={`${PRODUCT_CARD_CTA_ROW} bg-gradient-to-r from-rose-500 via-rose-500 to-orange-500 text-white shadow-md hover:shadow-lg disabled:cursor-not-allowed disabled:from-stone-300 disabled:via-stone-300 disabled:to-stone-400 disabled:shadow-none`}
            >
              <ShoppingCartIcon className="shrink-0" style={{ fontSize: 18 }} />
              <span className="whitespace-nowrap">{outOfStock ? "Sold out" : "Add"}</span>
            </button>
          )}
        </div>
      </div>
    </article>
  );
};

export default ProductCard;
